package com.steamstreet.vegasful.browser.account

import com.steamstreet.vegasful.browser.appScope
import com.steamstreet.vegasful.css.VegasfulStyles
import com.steamstreet.vegasful.css.css
import io.ktor.client.*
import io.ktor.client.engine.js.*
import io.ktor.client.request.*
import io.ktor.client.request.forms.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.sessionStorage
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.css.*
import kotlinx.css.properties.LineHeight
import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.js.onClickFunction
import kotlinx.html.js.onKeyDownFunction
import kotlinx.html.js.onSubmitFunction
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import org.w3c.dom.Element
import org.w3c.dom.ParentNode
import org.w3c.dom.asList
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.KeyboardEvent
import kotlin.js.Date

@Serializable
private data class EmailSignInResponse(
    val sent: Boolean = false,
    val error: String? = null
)

/**
 * Email capture: the pitch, an address field, and the request that mails a sign-in link.
 *
 * Written as a block that renders into any element so the same thing can sit in the overlay, on
 * the login page, or anywhere else worth asking from later. [source] is carried through to the
 * server so those placements stay tellable apart once there are more than one.
 */
class EmailCaptureForm(
    private val element: Element,
    private val source: String,
    private val onClose: (() -> Unit)? = null
) {
    private val client = HttpClient(JsClient())
    private val json = Json { ignoreUnknownKeys = true }

    fun render() {
        element.innerHTML = ""
        element.append {
            div {
                css {
                    "input" {
                        width = 100.pct
                        boxSizing = BoxSizing.borderBox
                        border = Border(1.px, BorderStyle.solid, VegasfulStyles.gray)
                        borderRadius = 5.px
                        padding = Padding(12.px)
                        fontSize = 16.px
                        fontWeight = FontWeight.w300
                    }
                }

                h2 {
                    css {
                        fontSize = 22.px
                        margin = Margin(0.px, 0.px, 8.px, 0.px)
                    }
                    +"Unlock your perfect Vegas"
                }
                p("email-capture-pitch") {
                    css {
                        fontSize = 15.px
                        lineHeight = LineHeight("1.4")
                        margin = Margin(0.px, 0.px, 20.px, 0.px)
                    }
                    // Sells the outcome and stays quiet about the mechanics. How signing in
                    // actually works is explained after they commit, in showSent() -- leading with
                    // it asks someone to evaluate a process before they want the thing at all.
                    //
                    // The headline is the positioning line and the pitch follows
                    // `docs/positioning.md` at the monorepo root. Neither promises a cadence: the
                    // weekly email exists but has not been enrolled, and "every Thursday" is a lie
                    // until it has.
                    +"The shows worth building a night around, the openings before they're busy, and the one-offs that never come back. Straight to your inbox."
                }

                form {
                    onSubmitFunction = { event ->
                        event.preventDefault()
                        submit()
                    }
                    input(type = InputType.email, classes = "email-capture-input") {
                        attributes["placeholder"] = "you@example.com"
                        attributes["autocomplete"] = "email"
                        attributes["name"] = "email"
                        onKeyDownFunction = { event ->
                            if ((event as KeyboardEvent).keyCode == 13) {
                                event.preventDefault()
                                submit()
                            }
                        }
                    }

                    div("email-capture-error") {
                        css {
                            display = Display.none
                            color = VegasfulStyles.pink
                            fontSize = 14.px
                            marginTop = 8.px
                        }
                    }

                    button(classes = "email-capture-submit") {
                        css {
                            width = 100.pct
                            marginTop = 12.px
                            +VegasfulStyles.largeButton
                            // largeButton inherits standardButton's #68e1ff, which on a primary
                            // call to action reads as a hyperlink rather than a button label.
                            // Overridden here rather than in the shared style, which the whole
                            // site uses.
                            color = Color.white
                        }
                        +SUBMIT_LABEL
                    }
                }

                if (onClose != null) {
                    a(href = "#", classes = "email-capture-dismiss") {
                        css {
                            display = Display.block
                            textAlign = TextAlign.center
                            marginTop = 16.px
                            fontSize = 14.px
                            color = VegasfulStyles.lightGray
                        }
                        onClickFunction = {
                            it.preventDefault()
                            onClose.invoke()
                        }
                        +"No thanks"
                    }
                }
            }
        }
    }

    private fun input() = element.querySelector(".email-capture-input") as? HTMLInputElement
    private fun submitButton() = element.querySelector(".email-capture-submit") as? HTMLElement
    private fun errorElement() = element.querySelector(".email-capture-error") as? HTMLElement

    private fun showError(message: String) {
        errorElement()?.let {
            it.textContent = message
            it.style.display = "block"
        }
    }

    private fun clearError() {
        errorElement()?.style?.display = "none"
    }

    private fun submit() {
        val address = input()?.value?.trim().orEmpty()
        if (address.isEmpty()) {
            showError("Please enter your email address.")
            return
        }

        clearError()
        val button = submitButton()
        button?.textContent = "One moment…"
        button?.setAttribute("disabled", "true")

        appScope.launch {
            val result = try {
                // Absolute, as everywhere else the client calls back: a bare path leaves the Ktor
                // URL builder on its default host rather than the page's own.
                val response = client.post("${window.location.origin}/auth/email/signin") {
                    accept(ContentType.Application.Json)
                    setBody(FormDataContent(parameters {
                        append("email", address)
                        append("source", source)
                        append("returnUrl", window.location.pathname + window.location.search)
                        // In the body, not the Accept header: CloudFront does not forward Accept
                        // to this origin, so negotiating on it works everywhere except in front
                        // of real users.
                        append("format", "json")
                    }))
                }
                json.decodeFromString<EmailSignInResponse>(response.bodyAsText())
            } catch (t: Throwable) {
                EmailSignInResponse(sent = false, error = "We couldn't send that just now. Please try again.")
            }

            if (result.sent) {
                // Recorded before the link is clicked on purpose: someone who has been sent a link
                // has done what was asked of them, and asking again on the next page would be
                // nagging a person who is at that moment reading our email.
                localStorage.setItem(CAPTURE_STATE_KEY, CAPTURE_STATE_REQUESTED)
                showSent(address)
            } else {
                button?.textContent = SUBMIT_LABEL
                button?.removeAttribute("disabled")
                showError(result.error ?: "We couldn't send that just now. Please try again.")
            }
        }
    }

    private fun showSent(address: String) {
        element.innerHTML = ""
        element.append {
            div {
                css {
                    textAlign = TextAlign.center
                }
                h2 {
                    css {
                        fontSize = 22.px
                        margin = Margin(0.px, 0.px, 12.px, 0.px)
                    }
                    +"Check your email"
                }
                // The mechanics are explained here rather than in the pitch: now that they've
                // asked for it, how it works is useful instead of something to weigh up.
                p {
                    css {
                        fontSize = 15.px
                        lineHeight = LineHeight("1.4")
                        margin = Margin(0.px, 0.px, 12.px, 0.px)
                    }
                    +"We just sent a link to "
                    strong { +address }
                    +". Open it and you're signed in — there's no password to set and nothing else to fill in."
                }
                p {
                    css {
                        fontSize = 13.px
                        lineHeight = LineHeight("1.4")
                        color = VegasfulStyles.lightGray
                        margin = Margin(0.px, 0.px, 20.px, 0.px)
                    }
                    +"The link works once and expires shortly. If it hasn't arrived in a minute, have a look in your spam folder."
                }
                if (onClose != null) {
                    button {
                        css {
                            width = 100.pct
                            +VegasfulStyles.largeButton
                            color = Color.white
                        }
                        onClickFunction = { onClose.invoke() }
                        +"Got it"
                    }
                }
            }
        }
    }
}

/**
 * Wire up every capture container inside [root] that isn't already running.
 *
 * `loginOptions()` only draws the empty container; something has to fill it. Anywhere a login
 * dialog is rendered has to call this, or the dialog shows an "or" divider with nothing above it.
 * Idempotent, so calling it again after re-rendering part of a page is safe.
 */
fun initEmailCapture(root: ParentNode, source: String) {
    root.querySelectorAll(".email-capture-container").asList().forEach { node ->
        (node as? Element)?.let { container ->
            if (container.getAttribute(INITIALIZED_ATTRIBUTE) != "1") {
                container.setAttribute(INITIALIZED_ATTRIBUTE, "1")
                EmailCaptureForm(container, source).render()
            }
        }
    }
}

/**
 * Count this page view and, on the first navigation after arriving, offer the capture overlay.
 *
 * Deliberately not on the landing page. Someone who has just arrived from a search result has not
 * yet seen anything worth subscribing for, and interrupting them there is how a bounce happens.
 * By the second page they have chosen to keep reading, which is the first moment the ask is
 * anything other than an obstacle.
 */
fun emailCaptureOverlay() {
    val pageViews = (sessionStorage.getItem(PAGE_VIEW_KEY)?.toIntOrNull() ?: 0) + 1
    sessionStorage.setItem(PAGE_VIEW_KEY, pageViews.toString())

    if (pageViews < 2) return
    if (sessionStorage.getItem(SHOWN_THIS_VISIT_KEY) != null) return
    if (Account.isLoggedIn()) return
    if (!isCaptureAllowed()) return

    // There used to be a check here standing this overlay down when the app-install interstitial
    // was on screen, because two overlays on one page is not an offer, it is a wall. The
    // interstitial is now a bar pinned to the bottom of the page rather than a modal, so the two
    // no longer compete for the screen: the bar sits below this overlay's backdrop and is simply
    // unreachable while it is up, which is the right behaviour without needing to be arranged.

    showCaptureOverlay()
}

/**
 * Whether the visitor is still open to being asked. Requesting a link settles it for good;
 * dismissing only buys quiet for a while, since a "no" now is not a "no" in two months.
 */
private fun isCaptureAllowed(): Boolean {
    return when (val state = localStorage.getItem(CAPTURE_STATE_KEY)) {
        null -> true
        CAPTURE_STATE_REQUESTED -> false
        else -> {
            val dismissedAt = state.toDoubleOrNull() ?: return false
            Date.now() - dismissedAt > DISMISS_QUIET_PERIOD_MS
        }
    }
}

private fun showCaptureOverlay() {
    sessionStorage.setItem(SHOWN_THIS_VISIT_KEY, "1")

    val overlay = document.createElement("div")
    overlay.id = OVERLAY_ID

    fun dismiss() {
        localStorage.setItem(CAPTURE_STATE_KEY, Date.now().toString())
        overlay.parentElement?.removeChild(overlay)
    }

    overlay.append {
        div {
            css {
                position = Position.fixed
                top = 0.px
                left = 0.px
                width = 100.pct
                height = 100.pct
                backgroundColor = Color.black.withAlpha(0.8)
                zIndex = VegasfulStyles.zIndexMenu + 1
                display = Display.flex
                justifyContent = JustifyContent.center
                alignItems = Align.center
                padding = Padding(20.px)
                boxSizing = BoxSizing.borderBox
            }

            // Clicking the backdrop is a dismissal; clicking the panel is not.
            onClickFunction = { event ->
                if (event.target == event.currentTarget) {
                    dismiss()
                }
            }

            div("email-capture-panel") {
                css {
                    +VegasfulStyles.popupWindow
                    backgroundColor = VegasfulStyles.darkBlue
                    color = VegasfulStyles.textColor
                    borderRadius = 10.px
                    padding = Padding(24.px)
                    width = 100.pct
                    maxWidth = 400.px
                }
            }
        }
    }

    document.body?.appendChild(overlay)

    overlay.querySelector(".email-capture-panel")?.let {
        EmailCaptureForm(it, source = "web-overlay", onClose = { dismiss() }).render()
    }
}

private const val OVERLAY_ID = "vegasful-email-capture"
private const val PAGE_VIEW_KEY = "vegasful_visit_page_views"
private const val SHOWN_THIS_VISIT_KEY = "vegasful_email_capture_shown"
private const val CAPTURE_STATE_KEY = "vegasful_email_capture"
private const val CAPTURE_STATE_REQUESTED = "requested"
private const val INITIALIZED_ATTRIBUTE = "data-email-capture-initialized"

/**
 * The call to action. Names what they get, not what we do -- "send me the link" asks someone to
 * agree to a mechanism, which is not the thing anyone actually wants.
 *
 * It also promises something built for them, which the emails have to eventually be worth. Worth
 * revisiting alongside whatever the retention sends actually turn out to say.
 */
private const val SUBMIT_LABEL = "Start my Vegas list"

/** How long a dismissal is honoured before the offer comes back. */
private const val DISMISS_QUIET_PERIOD_MS = 30.0 * 24 * 60 * 60 * 1000
